import { defineElement, FC, onDidLoad, onDidUnload, useElement, useProp } from '@atomify/hooks';
import { inviewObserver } from '@utilities/inview';

import createImageUrl from './create-image-url';
import hasResponsiveImages from './detect-responsive-images';
import ObjectFit from './object-fit';

const LAZY_IMAGE_ANIMATE_IN_CLASS = 'image--is-loaded';

const SETTINGS_ATTRIBUTE = 'data-settings';
const BASE_IMAGE_ATTRIBUTE = 'data-image';

export interface ImageSetting {
    width?: number;
    height?: number;
    src?: boolean | undefined;
}

// Modes are based on the image resizing module used in Umbraco
// Documentation and options: https://imageprocessor.org/imageprocessor-web/imageprocessingmodule/resize/
export type ImageMode = 'crop' | 'max' | 'min';

export interface ImageSettings {
    [key: string]: ImageSetting;
}

const SUPPORTS_SRCSET = hasResponsiveImages.srcset || hasResponsiveImages.picture;

const BPDImage: FC = ({ element }) => {
    const [mode] = useProp<ImageMode>('mode', 'crop');
    const image = useElement<HTMLImageElement>('img');

    onDidLoad(() => {
        inviewObserver.observe(element, (inView) => inView && loadImage());
    });

    onDidUnload(() => inviewObserver.unobserve(element));

    function generateSrc(baseImage: string, settings: ImageSettings) {
        const keys = Object.keys(settings);

        // Set setting to first one by default
        let setting = settings[keys[0]];

        // Overwrite when a setting is found with scr: true
        if (keys.length > 1) {
            const srcKey = keys.find((key) => !!settings[key].src);

            setting = srcKey ? settings[srcKey] : ({} as ImageSetting);
        }

        const src = createImageUrl({ image: baseImage, setting, mode });

        return src;
    }

    function generateSrcset(baseImage: string, settings: ImageSettings) {
        const keys = Object.keys(settings);

        const srcset = keys.reduce((srcset, key, index) => {
            const setting = settings[key];
            const url = createImageUrl({ image: baseImage, setting, mode });
            const isLast = index + 1 === keys.length;

            return `${srcset} ${url} ${key}${isLast ? '' : ','}`;
        }, '');

        return srcset;
    }

    function loadImage() {
        if (!image.current) return;

        inviewObserver.unobserve(element);

        const baseImage = image.current.getAttribute(BASE_IMAGE_ATTRIBUTE) as string;
        const settingsAttribute = image.current.getAttribute(SETTINGS_ATTRIBUTE);

        if (settingsAttribute) {
            const settings = JSON.parse(settingsAttribute) || {};

            const src = generateSrc(baseImage, settings);
            const srcset = generateSrcset(baseImage, settings);

            // // If there is no data-src set just render the element.
            if (!src) {
                renderImage(element);
                return;
            }

            if (SUPPORTS_SRCSET && srcset) {
                image.current.srcset = srcset;
            }

            image.current.src = src;
            image.current.onload = () => renderImage(element);
        } else {
            renderImage(element);
        }
    }

    function renderImage(element: HTMLElement) {
        if (!image.current) return;
        element.classList.add(LAZY_IMAGE_ANIMATE_IN_CLASS);

        if (!hasResponsiveImages.currentSrc) {
            (image.current as any).currentSrc = `${image.current.src}`;
        }

        ObjectFit.polyfillObjectFit(image.current);
    }
};

BPDImage.props = {
    mode: {
        type: String,
    },
};

defineElement('bpd-image', BPDImage);
